
import {Component, Vue} from "vue-property-decorator";
import {mapState} from "vuex";
import {
  CUSTOMERS_ACTIVE,
  CUSTOMERS_EDIT,
  CUSTOMERS_FETCH,
  CUSTOMERS_NEW,
  IMPERSONATE_USER,
  TRANSLATIONS_FETCH,
} from "@/store/ActionTypes";
import CustomerListItem from "@/models/Customer/CustomerListItem";

import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import CustomerNew from "@/models/Customer/CustomerNew";
import CustomerEdit from "@/models/Customer/CustomerEdit";

import {UserTypes} from "@/models/User/UserTypes";
import CustomersRepository from "@/utils/API/CustomersRepository";
import {RestaurantEmployee} from "@/models/Customer/RestaurantEmployee";

import vManageCustomerModal from "@/components/vManageCustomerModal.vue";
import vToggleSwitch from "@/components/vToggleSwitch.vue";
import vToggleSwitchSelfHandled from "@/components/vToggleSwitchSelfHandled.vue";
import vModal from "@/components/vModal.vue";
import vTable from "@/components/vTable.vue";
import vPreLoader from "@/components/vPreLoader.vue";
import SubscriptionRepository from "../../utils/API/SubscriptionRepository";
import {SubscriptionRecurrenceType} from "../../models/Subscriptions/SubscriptionRecurrenceType";
import {getErrorMessage} from "@/utils/Helpers";
import {dateDisplayFormat} from "@/utils/Defaults";
import {SubscriptionStatus} from "../../models/Subscriptions/SubscriptionStatus";
import vGenericConfirmationModal from "@/components/vGenericConfirmationModal.vue";
import {CustomerCreationMode} from "@/models/Customer/CustomerCreationMode";
import {NOT_APPLICABLE} from "@/utils/constants";

@Component({
  components: {
    vTable,
    vManageCustomerModal,
    vModal,
    vToggleSwitch,
    vToggleSwitchSelfHandled,
    vPreLoader,
    vGenericConfirmationModal,
  },
  computed: {
    ...mapState({
      customersState: (state: any) => state.CustomersModule,
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class Customers extends Vue {
  customersState: any;
  localizationState: any;

  /**
   * vTable properties
   */
  selectErase = false;
  searchable = true;
  editableTable = true;
  loading = false;

  filters: Filter[] = [];
  ordering: Ordering = { field: "", orderType: OrderType.Asc };
  page: number = 1;
  nonArchivedOnly: boolean = true;

  /**
   * Modal Properties
   */
  showModal = false;
  showMembersModal = false;
  modalTitle = "";
  showDeleteRestaurantPrompt = false;
  showDeleteMemberPrompt = false;
  showEnableMemberPrompt = false;
  deleteModalMessage = "";
  showTrialPeriodModal = false;

  clickedRestaurantId: number = 0;
  trialEndDate: any;

  /**
   * If true, vManageCustomerModal will populate with
   * restaurant info for selected customer,
   * otherwise it's in create mode
   */
  editMode = false;

  /**
   * Customer and Restaurant member management
   * models and helper variables.
   */
  customer: CustomerNew = {
    name: "",
    phone: "",
    ownerEmail: "",
    ownerPassword: "",
    confirmOwnerPassword: "",
    ownerFirstName: "",
    ownerLastName: "",
    specialSubscriptionEndDate: null,
    accountType: 0,
    numberOfUsers: 0,
    subscriptionRecurrenceType: SubscriptionRecurrenceType.Yearly,
    chosenNutrientSource: 0,
    remainingLabels: 0,
    microsoftEntraTenantId: "",
    signInMethod: 0,
  };
  currentCustomerId = 0;

  //
  currentCustomerEnabled = true;
  restaurantId: number | null = null;
  selectedEmployeeId: number = 0;
  memberRole: UserTypes = UserTypes.Chef;
  restaurantEmployee: RestaurantEmployee = {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    isEnabled: true,
    userType: UserTypes.Chef,
    signInMethod: 0,
  };
  employeePassword: string = "";
  employeeConfirmPassword: string = "";
  restaurantEmployees: RestaurantEmployee[] = [];
  expiryDate = this.customer.specialSubscriptionEndDate;

  employeesLoading: boolean = false;
  emailPasswordSignInMethod = 0;
  googleSignInMethod = 1;
  microsoftSignInMethod = 3;

  // Choose archived or not
  toggleArchived() {
    this.nonArchivedOnly = !this.nonArchivedOnly;
    let nonArchivedFilter = this.filters.find(
      (x: any) => x.name === "NonArchivedOnly"
    );
    if (nonArchivedFilter) {
      nonArchivedFilter.value = this.nonArchivedOnly.toString();
    }
    else {
      this.filters.push({
        name: "NonArchivedOnly",
        value: this.nonArchivedOnly.toString(),
      });
    }
    this.search(this.filters, this.ordering);
  }

  /**
   * Deleting restaurant member when admin confirms.
   * NOTE: This SOFT DELETES the member
   */
  deleteMember() {
    this.$snotify.async(
      this.translations.get("customers_toastr_deleting_employee"),
      () =>
        new Promise((resolve, reject) => {
          CustomersRepository.deleteRestaurantEmployee(
            // restaurantEmployee is the selected member from members management modal
            this.restaurantEmployee.id
          )
            .then(() => {
              this.loading = true;
              resolve({
                body: this.translations.get(
                  "customers_toastr_deleted_employee"
                ),
              });

              // update the grid after successful deletion
              CustomersRepository.listRestaurantEmployees(
                new ListRequest({ page: 1, pageSize: 999 }),
                this.restaurantId ? this.restaurantId : 0
              )
                .then((response) => {
                  this.restaurantEmployees = response.data.result;
                  this.restaurantEmployee = this.restaurantEmployees.find(
                    (e) => e.id === this.restaurantEmployee.id
                  );
                  this.employeesLoading = false;
                  this.loading = false;
                });

              this.showDeleteMemberPrompt = false;
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 5000,
        closeOnClick: true,
      }
    );
  }

  enableMember() {
    this.$snotify.async(
      this.translations.get("customers_toastr_enabling_employee"),
      () =>
        new Promise((resolve, reject) => {
          CustomersRepository.enableRestaurantEmployee(
            // restaurantEmployee is the selected member from members management modal
            this.restaurantEmployee.id
          )
            .then(() => {
              this.loading = true;
              resolve({
                body: this.translations.get(
                  "customers_toastr_enabled_employee"
                ),
              });

              // update the grid after re-enabling
              CustomersRepository.listRestaurantEmployees(
                new ListRequest({ page: 1, pageSize: 999 }),
                this.restaurantId ? this.restaurantId : 0
              )
                .then((response) => {
                  this.restaurantEmployees = response.data.result;
                  this.restaurantEmployee = this.restaurantEmployees.find(
                    (e) => e.id === this.restaurantEmployee.id
                  );
                  this.employeesLoading = false;
                  this.loading = false;
                });

              this.showEnableMemberPrompt = false;
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 5000,
        closeOnClick: true,
      }
    );
  }


  /**
   * Deleting restaurant after admin confirms
   */
  deleteRestaurant() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("customers_toastr_deleting_restaurant"),
      () =>
        new Promise((resolve, reject) => {
          // currentCustomerId is the id of the selected restaurant which is being edited
          CustomersRepository.deleteRestaurant(this.currentCustomerId)
            .then((response) => {
              if (response.data.result === null)
                resolve({
                  body: this.translations.get(
                    "customers_toastr_deleted_restaurant"
                  ),
                });
              else
                resolve({
                  body: response.data.result,
                });
              this.resetState();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 5000,
        closeOnClick: true,
      }
    );
  }

  // DatePicker
  // Datepicker option object to disable dates before today
  disabledDates = {
    to: new Date(),
  };

  /**
   * Dispatch action to the store to impersonate user
   * if successful, redirect to user's home.
   *
   * @param {number} id: id of the impersonated user
   */
  impersonate(id: number) {
    this.$store.dispatch(IMPERSONATE_USER, id).then(() => {
      this.$router.push("/");
    });
  }

  /**
   * Combined method for saving and employee, whether it's new or edit employee.
   */
  saveEmployee() {
    // Validate fields before
    this.$validator.validateAll("addEmployee").then((result) => {
      if (result) {
        // Check if it's not edit mode (is new employee mode)
        if (!this.isEditEmployeeMode) {
          CustomersRepository.createRestaurantEmployee({
            restaurantId: this.restaurantId ? this.restaurantId : 0,
            email: this.restaurantEmployee.email,
            firstName: this.restaurantEmployee.firstName,
            lastName: this.restaurantEmployee.lastName,
            isEnabled: this.restaurantEmployee.isEnabled,
            userType: this.restaurantEmployee.userType,
            signInMethod: this.restaurantEmployee.signInMethod,
            password: this.employeePassword,
            confirmPassword: this.employeeConfirmPassword,
          })
            .then((response) => {
              this.restaurantEmployees.push(response.data.result);
              this.newEmployee();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              this.$snotify.error(message);
            });
        }
        else {
          // It is edit mode
          CustomersRepository.updateRestaurantEmployee({
            email: this.restaurantEmployee.email,
            firstName: this.restaurantEmployee.firstName,
            lastName: this.restaurantEmployee.lastName,
            isEnabled: this.restaurantEmployee.isEnabled,
            userType: this.restaurantEmployee.userType,
            userId: this.selectedEmployeeId,
            signInMethod: this.restaurantEmployee.signInMethod,
            password: this.employeePassword,
            confirmPassword: this.employeeConfirmPassword,
          })
            .then(() => {
              this.$snotify.success("Successfully updated employee!");

              // if successful update
              // find the selected member (it's reference in the list)
              // and update it's info on frontend instead of fetching again
              const emp = this.restaurantEmployees.find(
                (e) => e.id === this.selectedEmployeeId
              );
              if (emp) {
                emp.firstName = this.restaurantEmployee.firstName;
                emp.lastName = this.restaurantEmployee.lastName;
                emp.email = this.restaurantEmployee.email;
                emp.isEnabled = this.restaurantEmployee.isEnabled;
                emp.userType = this.restaurantEmployee.userType;
                emp.signInMethod = this.restaurantEmployee.signInMethod;
              }
            })
            // regular catch :)
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              this.$snotify.error(message);
            });
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  onChangeEndTrialPeriod() {
    SubscriptionRepository.extendTrialSubscription({
      restaurantId: this.clickedRestaurantId,
      trialEndDate: this.expiryDate,
    })
      .then(() => {
        this.$snotify.success("Trial end date has been successfully changed.");
        this.showTrialPeriodModal = false;
        this.resetState();
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("toastr_network_error");
        }
        this.$snotify.error(message);
      });
  }

  closeTrialPeriodModal() {
    this.showTrialPeriodModal = false;
  }

  /**
   * Getter that returns true if there's selected employee from the list
   * False if the selected item is  + New Member
   *
   * @returns {boolean} true if there's selected employee from the list
   */
  get isEditEmployeeMode() {
    return this.selectedEmployeeId !== 0;
  }

  get isEmailPasswordEmployee() {
    return this.restaurantEmployee.signInMethod === this.emailPasswordSignInMethod;
  }

  /**
   * Set selectedEmployeeId to the id of selected member.
   * Set restaurantEmployee to the member with id from parameters
   *
   * @param {number} id Id of the selected member
   */
  selectEmployee(id: number) {
    this.selectedEmployeeId = id;
    let emp = this.restaurantEmployees.find((e) => e.id === id);
    if (emp) {
      this.restaurantEmployee = {
        ...emp,
        signInMethod: emp.signInMethod === 2 ? this.googleSignInMethod : emp.signInMethod,
      };
    }
    // and of course
    this.employeePassword = "";
    this.employeeConfirmPassword = "";
  }

  /**
   * Method that resets binded fields in the form
   * Invoked if New Member is selected
   */
  newEmployee() {
    this.selectedEmployeeId = 0;
    this.restaurantEmployee = {
      id: 0,
      firstName: "",
      lastName: "",
      email: "",
      isEnabled: true,
      userType: UserTypes.Chef,
      signInMethod: this.emailPasswordSignInMethod,
    };

    this.employeePassword = "";
    this.employeeConfirmPassword = "";
    this.$validator.reset(); // THIS RESETS THE VALIDATION :)
    this.memberRole = UserTypes.Chef;
  }

  /**
   * Opens the member management modal and populates it
   * with employees from the restaurant with id from parameters
   *
   * @param {number} id Restaurant Id
   */
  manageMembers(id: number) {
    this.showMembersModal = true;
    this.restaurantId = id;
    this.employeesLoading = true;
    this.deleteModalMessage = this.translations.get(
      "customers_modal_delete_member"
    );
    CustomersRepository.listRestaurantEmployees(
      new ListRequest({ page: 1, pageSize: 999 }),
      id
    ).then((response) => {
      this.restaurantEmployees = response.data.result;
      this.employeesLoading = false;
    });
  }

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push(args[3]);

    this.loading = true;

    this.$store
      .dispatch(
        CUSTOMERS_FETCH,
        new ListRequest({
          page: args[0],
          pageSize: 25,
          filters: args[2],
          orders: orderings,
        })
      )
      .then(() => {
        this.loading = false;
        this.page = args[0];
        this.filters = args[2];
        this.ordering = args[3];
      });
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.loading = true;
    this.$store
      .dispatch(
        CUSTOMERS_FETCH,
        new ListRequest({
          page: 1,
          pageSize: this.customersState.pageSize,
          filters,
          orders: orderings,
        })
      )
      // go to page 1 and leave the filters and orders the same
      .then(() => {
        this.loading = false;
        this.page = 1;
        this.filters = filters;
        this.ordering = ordering;
      });
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.loading = true;
    this.$store
      .dispatch(
        CUSTOMERS_FETCH,
        new ListRequest({
          page: this.customersState.page,
          pageSize: this.customersState.pageSize,
          filters,
          orders: orderings,
        })
      )
      // don't change the page
      .then(() => {
        this.loading = false;
        this.page = this.customersState.page;
        this.filters = filters;
        this.ordering = ordering;
      });
  }

  /**
   * dadsdsdd
   */
  addNewCustomer() {
    let newCustomer = {
      ...this.customer,
      ownerPassword: this.customer.signInMethod === this.emailPasswordSignInMethod
        ? this.customer.ownerPassword
        : "",
      confirmOwnerPassword: this.customer.signInMethod === this.emailPasswordSignInMethod
        ? this.customer.confirmOwnerPassword
        : "",
      microsoftEntraTenantId: this.customer.signInMethod === this.microsoftSignInMethod
        ? this.customer.microsoftEntraTenantId
        : "",
    };
    // show toastr and do the request through vuex store
    // (don't hate me for this, I didn't know back then )
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("customers_toastr_adding"),
      () =>
        new Promise((resolve, reject) => {
          this.$store
            .dispatch(CUSTOMERS_NEW, newCustomer)
            .then(() => {
              this.resetState();
              resolve({
                body: this.translations.get("customers_toastr_added"),
              });

              this.$store.dispatch(
                CUSTOMERS_FETCH,
                new ListRequest({
                  page: this.page,
                  pageSize: 25,
                  filters: this.filters,
                  orders: [this.ordering],
                })
              );
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 5000,
        closeOnClick: true,
      }
    );
  }

  /**
   * Method that resets most variables and closes all modals.
   * Fetching customers for update
   */
  resetState() {
    this.customer = {
      name: "",
      phone: "",
      ownerEmail: "",
      ownerPassword: "",
      confirmOwnerPassword: "",
      ownerFirstName: "",
      ownerLastName: "",
      specialSubscriptionEndDate: null,
      accountType: 0,
      numberOfUsers: 0,
      subscriptionRecurrenceType: SubscriptionRecurrenceType.Yearly,
      microsoftEntraTenantId: "",
      signInMethod: this.emailPasswordSignInMethod,
    };
    this.showModal = false;
    this.editMode = false;
    this.currentCustomerId = 0;
    this.currentCustomerEnabled = true;
    this.modalTitle = this.translations.get("customers_modal_title_add");
    this.showMembersModal = false;
    this.showTrialPeriodModal = false;

    this.restaurantEmployee = {
      id: 0,
      firstName: "",
      lastName: "",
      email: "",
      isEnabled: true,
      userType: UserTypes.Chef,
      signInMethod: this.emailPasswordSignInMethod,
    };
    this.restaurantEmployees.length = 0;
    this.restaurantId = 0;
    this.selectedEmployeeId = 0;
    this.employeePassword = "";
    this.employeeConfirmPassword = "";

    this.$store.dispatch(
      CUSTOMERS_FETCH,
      new ListRequest({
        page: this.page,
        pageSize: 25,
        filters: this.filters,
        orders: [this.ordering],
      })
    );

    this.showDeleteRestaurantPrompt = false;
    this.showDeleteMemberPrompt = false;
  }

  /**
   * Method firing from Restaurant edit / add modal
   */
  editCustomerEnabled(value: boolean) {
    this.currentCustomerEnabled = value;
  }

  /**
   * Method that populates the form for edit and shows the modal
   *
   * @param {number} id
   */
  editCustomer(id: number) {
    const customer = this.customersState.customerList.find(
      (cust: CustomerListItem) => cust.id === id
    );

    this.customer.name = customer.name;
    this.customer.phone = customer.phone;
    this.customer.ownerEmail = customer.ownerEmail;
    this.customer.ownerFirstName = customer.ownerFirstName;
    this.customer.ownerLastName = customer.ownerLastName;
    this.customer.microsoftEntraTenantId = customer.microsoftEntraTenantId || "";

    this.customer.downloadedLabels =
      customer.currentSubscription.downloadedLabels;
    this.customer.remainingLabels = customer.remainingLabels;
    this.customer.labelsLimit =
      customer.currentSubscription.subscriptionPackage.labelsLimit;
    this.customer.nextResetDate = customer.currentSubscription.nextResetDate;

    this.currentCustomerId = customer.id;
    this.currentCustomerEnabled = !customer.isDisabled;

    this.showModal = true;
    this.editMode = true;
    this.modalTitle = this.translations.get("customers_modal_title_edit");
    this.deleteModalMessage = this.translations.get(
      "customers_modal_delete_restaurant"
    );
  }

  /**
   * Prepare edit customer model and dispatch action for edit.
   */
  editCustomerSubmit() {
    let editCustomer = new CustomerEdit(
      this.currentCustomerId,
      this.customer.name,
      this.customer.phone,
      this.customer.downloadedLabels,
      this.customer.remainingLabels,
      this.customer.microsoftEntraTenantId
    );

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("customers_toastr_editing"),
      () =>
        new Promise((resolve, reject) => {
          this.$store
            .dispatch(CUSTOMERS_EDIT, editCustomer)
            .then(() => {
              this.$store
                .dispatch(CUSTOMERS_ACTIVE, {
                  restaurantId: editCustomer.id,
                  enabled: this.currentCustomerEnabled,
                })
                .then(() => {
                  // if successful fetch for changes
                  this.$store.dispatch(
                    CUSTOMERS_FETCH,
                    new ListRequest({
                      page: this.page,
                      pageSize: 25,
                      filters: this.filters,
                      orders: [this.ordering],
                    })
                  );

                  this.resetState();

                  resolve({
                    body: this.translations.get("customers_toastr_edited"),
                  });
                })
                .catch(() => {
                  reject({
                    body: this.translations.get(
                      "customers_toastr_editing_failed"
                    ),
                  });
                });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 5000,
        closeOnClick: true,
      }
    );
  }

  /**
   * Method for edit / add restaurant modal toggling
   */
  toggleModal() {
    this.showModal = !this.showModal;
    this.modalTitle = this.translations.get("customers_modal_title_add");
  }

  /**
   * Getter method to transform the list of restaurants to grid data (vTable)
   */
  get transformCustomers() {
    return this.customersState.customerList.map((element: CustomerListItem) => {
      let subscriptionName = "";
      if (element.currentSubscription.subscriptionPackage) {
        let subscriptionCycle =
          element.currentSubscription.subscriptionPackage.cycle ==
          SubscriptionRecurrenceType.Monthly
            ? "Monthly"
            : "Yearly";

        subscriptionName = `${element.currentSubscription.subscriptionPackage.name} ${subscriptionCycle}`;
      }

      if (element.currentSubscription.isTrial) {
        subscriptionName += " [Trial]";
      }
      if (element.creationMode == CustomerCreationMode.Direct) {
        subscriptionName = "Direct Signup";
      }

      return {
        e1: element.name ? element.name : "",
        e2: element.ownerLastName ? element.ownerLastName : "",
        e3: element.ownerFirstName ? element.ownerFirstName : "",
        e4: element.phone ? element.phone : "",
        e5: element.industry ? element.industry.name : "",
        e6: element.creationMode == CustomerCreationMode.Direct && element.currentSubscription.isTrial
          ? 0
          : element.currentSubscription.subscriptionPackageId,
        e7: subscriptionName,
        e8:
          element.currentSubscription.status === SubscriptionStatus.Active
            ? {
              component: "div",
              props: { class: "label label-success text-center" },
              html: this.translations.get("active"),
            }
            : {
              component: "div",
              props: { class: "label label-danger text-center" },
              html: this.translations.get("inactive"),
            },

        e9: element.creationMode == CustomerCreationMode.Direct ? NOT_APPLICABLE : this.$moment(element.trialStartDate).format(dateDisplayFormat),
        e10: element.creationMode == CustomerCreationMode.Direct ? NOT_APPLICABLE : this.$moment(element.trialEndDate).format(dateDisplayFormat),
        e11: element.lastLoggedInOn
          ? this.$moment(element.lastLoggedInOn).format(dateDisplayFormat)
          : "N\\A",
        e12: element.numberOfMembers,
        e13: {
          component: "span",
          props: { id: element.id, class: "edit" },
          html: '<i title="Manage Members" class="fa fa-users"></i>',
          onClick: (id: number) => this.manageMembers(id),
        },
        e14: {
          component: "vToggleSwitchSelfHandled",
          props: {
            id: element.id,
            item: element,
            isChecked: element.isArchived,
          },
          events: {
            "vToggleSwitchSelfHandled:changed": (item: any) => {
              let isArchived = !item.isArchived;
              CustomersRepository.updateRestaurantArchiveStatus(
                item.id,
                isArchived
              )
                .then(() => {
                  this.search(this.filters, this.ordering);
                })
                .catch((error) => {
                  this.loading = false;
                  this.$snotify.error(
                    getErrorMessage(error, this.translations)
                  );
                });
            },
          },
        },
        e15: element.currentSubscription.isTrial
          ? {
            component: "span",
            props: {
              style: "font-size: 17px; color: #0288d1; cursor: pointer;",
            },
            html: '<i title="Is trial" class="fa fa-user"></i>',
            onClick: (id: number) => {
              this.showTrialPeriodModal = true;
              this.clickedRestaurantId = id;
            },
          }
          : {
            component: "span",
            props: {
              style: "font-size: 17px; color: gray;",
              disabled: true,
            },
            html: '<i title="Is trial" class="fa fa-user"></i>',
          },
        meta: {
          id: element.id,
          link: element.ownerEmail,
          marked: element.isDisabled,
        },
      };
    });
  }

  mounted() {
    this.filters.push({
      name: "NonArchivedOnly",
      value: this.nonArchivedOnly.toString(),
    });
    this.search(this.filters, this.ordering);
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("email"),
        key: "OwnerEmail",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("customers_restaurant_name"),
        key: "Name",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("last_name"),
        key: "OwnerLastName",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("first_name"),
        key: "OwnerFirstName",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("phone"),
        key: "Phone",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("industry"),
        key: "OwnerIndustry",
        searchable: true,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("subscription_package_id"),
        key: "SubscriptionId",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("subscription"),
        key: "Subscription",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("isActive"),
        key: "IsActive",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("start-trial-date"),
        key: "trialStartDate",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("end-trial-date"),
        key: "trialEndDate",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("last-logged-in-date"),
        key: "lastLoggedInOn",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("number_of_members"),
        key: "NumberOfMembers",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: "Manage Members",
        key: "Members",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("isArchived"),
        key: "isArchived",
        searchable: false,
        searchString: "",
        sortable: false,
      },
      {
        name: this.translations.get("customer_isTrial"),
        key: "isTrial",
        searchable: false,
        searchString: "",
        sortable: false,
      },
    ];
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["customers", "common"],
    });

    document.title = "Customers | MenuSano";
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default Customers;
